import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import Nav from '../components/Nav';
import Footer from '../components/Footer';
import { BrowsePreview, ReservePreview, InvitePreview, RsvpPreview } from '../components/PhonePreviews';
import { supabase } from '../lib/supabase';
import type { FeaturedLocation } from '../lib/database.types';
import { formatMoney } from '../lib/format';

type Card = FeaturedLocation;

export default function Home() {
  return (
    <>
      <Nav />
      <Hero />
      <TeamPhoto />
      <Marquee />
      <SocialProof />
      <ForTheLead />
      <SectionDivider />
      <TeamPhotoTwo />
      <TeamPhotoThree />
      <OrganizerToolkit />
      <FeaturedLocations />
      <RequestLocationBanner />
      <FAQ />
      <CTAStrip />
      <Footer />
    </>
  );
}

function Hero() {
  return (
    <section style={{ paddingTop: 48, paddingBottom: 64 }}>
      <div className="container">
        <div className="eyebrow" style={{ marginBottom: 24 }}>Social booking platform for NYC teams</div>
        <h1
          className="display"
          style={{
            fontSize: 'clamp(64px, 12vw, 160px)',
            margin: '0 0 32px',
            lineHeight: 0.92,
          }}
        >
          The easiest way to{' '}
          <span className="serif-italic" style={{ color: 'var(--wine)' }}>happy hour</span>{' '}
          in NYC.
        </h1>
        <div className="hero-row">
          <p style={{ fontSize: 'clamp(18px, 2.2vw, 22px)', lineHeight: 1.45, color: 'var(--ink-soft)', maxWidth: 580, margin: 0 }}>
            Get your team out of the office. We handle the tab, the RSVPs, and the receipt.
          </p>
          <div className="hero-ctas">
            <Link to="/signup" className="btn btn-pop">Plan a social →</Link>
            <Link to="/for-restaurants" className="btn btn-ghost">For Restaurants / Bars</Link>
          </div>
        </div>
      </div>

      <style>{`
        .hero-row {
          display: grid;
          grid-template-columns: 1.2fr 1fr;
          gap: 48px;
          align-items: end;
          margin-top: 24px;
        }
        .hero-ctas {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          justify-content: flex-end;
        }
        @media (max-width: 720px) {
          .hero-row {
            grid-template-columns: 1fr;
            gap: 28px;
          }
          .hero-ctas {
            justify-content: flex-start;
            flex-direction: column;
            align-items: stretch;
          }
          .hero-ctas .btn { width: 100%; justify-content: center; }
        }
      `}</style>
    </section>
  );
}

function TeamPhoto() {
  return (
    <section style={{ padding: '0 0 64px' }}>
      <div className="container">
        <div
          style={{
            position: 'relative',
            width: '100%',
            aspectRatio: '16 / 9',
            minHeight: 280,
            borderRadius: 20,
            overflow: 'hidden',
            background: `url(/photos/team-pub.jpg) center/cover no-repeat, linear-gradient(135deg, oklch(0.45 0.14 22), oklch(0.62 0.16 48))`,
          }}
        >
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.55), rgba(0,0,0,0.05) 60%)' }} />
          <div className="team-photo-caption">
            <div className="serif-italic team-photo-quote">
              "Picked the bar in five minutes. Goaround did the rest."
            </div>
            <div className="mono" style={{ fontSize: 12, letterSpacing: '0.14em', textTransform: 'uppercase', opacity: 0.92, marginTop: 10 }}>
              — Lyla, Finance
            </div>
          </div>
        </div>
      </div>
      <style>{`
        .team-photo-caption { position: absolute; left: 40px; right: 40px; bottom: 32px; color: #fff; max-width: 540px; }
        .team-photo-quote { font-size: clamp(20px, 3.5vw, 44px); line-height: 1.1; margin-top: 10px; text-shadow: 0 2px 14px rgba(0,0,0,0.45); }
        @media (max-width: 720px) {
          .team-photo-caption { left: 20px; right: 20px; bottom: 20px; }
        }
      `}</style>
    </section>
  );
}

function ForTheLead() {
  // Reordered so the four steps map 1:1 with the phone previews stacked below each tile.
  const jobs: { tag: string; t: string; b: string; Phone: () => JSX.Element }[] = [
    { tag: 'Browse',  t: 'Find a spot.',     b: 'Curated locations across Manhattan. Filter by neighborhood, vibe, headcount.', Phone: BrowsePreview },
    { tag: 'Reserve', t: 'Lock it in.',      b: 'Pre-pay the package on your company card. One receipt, no tab to settle.',    Phone: ReservePreview },
    { tag: 'Invite',  t: 'Share the link.',  b: 'Drop it in Slack. Teammates RSVP in two taps, no Goaround account needed.',    Phone: InvitePreview },
    { tag: 'Track',   t: 'Real-time count.', b: 'Headcount climbs as RSVPs come in. Final count locks at 3pm day-of.',          Phone: RsvpPreview },
  ];
  return (
    <section className="section">
      <div className="container">
        <div style={{ marginBottom: 40 }}>
          <div className="eyebrow" style={{ marginBottom: 16 }}>For the team lead</div>
          <h2 className="display" style={{ fontSize: 'clamp(36px, 6vw, 76px)', margin: 0, maxWidth: 880 }}>
            All the credit, <span className="serif-italic" style={{ color: 'var(--wine)' }}>none of the legwork.</span>
          </h2>
        </div>
        <div className="r-grid-4" style={{ gap: 24 }}>
          {jobs.map(({ tag, t, b, Phone }) => (
            <div key={tag} style={{ display: 'flex', flexDirection: 'column' }}>
              <span className="mono" style={{ alignSelf: 'flex-start', fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', padding: '6px 10px', border: '1px solid var(--ink)', borderRadius: 999 }}>{tag}</span>
              <h3 className="display" style={{ fontSize: 26, margin: '16px 0 8px' }}>{t}</h3>
              <p style={{ color: 'var(--ink-soft)', fontSize: 15, margin: 0, lineHeight: 1.5 }}>{b}</p>
              <div style={{ marginTop: 20 }}>
                <Phone />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Marquee() {
  const items = [
    'No expense reports',
    'No Splitwise threads',
    'Real RSVPs, real time',
    'One link, one receipt',
    'Your team out of the office',
    'Your night, handled',
  ];
  const loop = [...items, ...items];
  return (
    <div className="marquee" style={{ background: 'var(--primary)', color: 'var(--ink)', padding: '20px 0', borderTop: '1.5px solid var(--ink)', borderBottom: '1.5px solid var(--ink)', overflow: 'hidden' }}>
      <div className="marquee-track">
        {loop.map((s, i) => (
          <span key={i} className="display" style={{ fontSize: 36, letterSpacing: '-0.03em', display: 'inline-flex', alignItems: 'center', gap: 48, whiteSpace: 'nowrap' }}>
            {s}
            <span style={{ width: 10, height: 10, background: 'var(--ink)', borderRadius: '50%' }} />
          </span>
        ))}
      </div>
    </div>
  );
}

function SectionDivider() {
  return (
    <div className="container">
      <hr style={{ border: 0, borderTop: '1.5px solid var(--ink)', margin: 0 }} />
    </div>
  );
}

function TeamPhotoTwo() {
  return (
    <section style={{ padding: '64px 0' }}>
      <div className="container">
        <div
          style={{
            position: 'relative',
            width: '100%',
            aspectRatio: '16 / 9',
            minHeight: 280,
            borderRadius: 20,
            overflow: 'hidden',
            // Layered: real photo on top, gradient fallback underneath so the
            // section still looks right while the file is pending in /public.
            background: 'url(/photos/team-selfie.jpg) center/cover no-repeat, linear-gradient(135deg, oklch(0.42 0.14 22), oklch(0.62 0.16 48))',
          }}
        >
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.55), rgba(0,0,0,0.05) 60%)' }} />
          <div className="team-photo-2-caption">
            <div className="serif-italic team-photo-2-quote">
              "We use Goaround to organize all our team socials."
            </div>
            <div className="mono" style={{ fontSize: 12, letterSpacing: '0.14em', textTransform: 'uppercase', opacity: 0.92, marginTop: 10 }}>
              — Blake, Customer Success
            </div>
          </div>
        </div>
      </div>
      <style>{`
        .team-photo-2-caption {
          position: absolute;
          right: 40px;
          bottom: 32px;
          left: 40px;
          color: #fff;
          max-width: 540px;
          margin-left: auto;
          text-align: right;
        }
        .team-photo-2-quote {
          font-size: clamp(20px, 3.5vw, 44px);
          line-height: 1.1;
          text-shadow: 0 2px 14px rgba(0,0,0,0.45);
        }
        @media (max-width: 720px) {
          .team-photo-2-caption { right: 20px; bottom: 20px; left: 20px; }
        }
      `}</style>
    </section>
  );
}

function TeamPhotoThree() {
  // Mobile-only extra social-proof photo, sits beneath TeamPhotoTwo with
  // 64px of padding on top (so 64px gap from the section above).
  return (
    <section className="show-mobile" style={{ padding: '0 0 64px' }}>
      <div className="container">
        <div
          style={{
            position: 'relative',
            width: '100%',
            aspectRatio: '16 / 9',
            minHeight: 280,
            borderRadius: 20,
            overflow: 'hidden',
            background: 'url(/photos/team-six.jpg) center/cover no-repeat, linear-gradient(135deg, oklch(0.44 0.14 250), oklch(0.66 0.14 30))',
          }}
        >
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.55), rgba(0,0,0,0.05) 60%)' }} />
          <div className="team-photo-3-caption">
            <div className="serif-italic team-photo-3-quote">
              "Booked our last team night in three taps."
            </div>
            <div className="mono" style={{ fontSize: 12, letterSpacing: '0.14em', textTransform: 'uppercase', opacity: 0.92, marginTop: 10 }}>
              — Jenn, Product
            </div>
          </div>
        </div>
      </div>
      <style>{`
        .team-photo-3-caption {
          position: absolute;
          left: 20px;
          right: 20px;
          bottom: 20px;
          color: #fff;
          max-width: 540px;
        }
        .team-photo-3-quote {
          font-size: clamp(20px, 3.5vw, 44px);
          line-height: 1.1;
          text-shadow: 0 2px 14px rgba(0,0,0,0.45);
        }
      `}</style>
    </section>
  );
}

function OrganizerToolkit() {
  const tools = [
    { title: 'Share link', desc: 'One URL handles RSVPs and reminders.' },
    { title: 'RSVP tracker', desc: "See who's in, who's out, who hasn't opened it." },
    { title: 'Calendar invite', desc: 'Auto-sent to everyone who RSVPs yes.' },
    { title: 'One receipt', desc: 'One clean line item for finance.' },
    { title: 'Slack reminders', desc: 'A "we\'re heading down" ping. (Soon.)' },
    { title: 'Cancel with grace', desc: 'Full refund up to 3pm day-of.' },
  ];
  return (
    // Hidden on mobile per the new flow: mobile goes Marquee → TeamPhotoTwo → Featured.
    <section className="section hide-mobile">
      <div className="container">
        <div className="r-split-narrow">
          <div>
            <div className="eyebrow" style={{ marginBottom: 16 }}>The toolkit</div>
            <h2 className="display" style={{ fontSize: 'clamp(32px, 5vw, 60px)', margin: 0 }}>
              The work nobody sees, <span className="serif-italic" style={{ color: 'var(--wine)' }}>done for you.</span>
            </h2>
          </div>
          <div className="r-grid-2">
            {tools.map((t) => (
              <div key={t.title} style={{ padding: 20, borderRadius: 18, background: 'var(--card)', border: '1px solid var(--line)' }}>
                <div className="display" style={{ fontSize: 20, marginBottom: 8 }}>{t.title}</div>
                <p style={{ color: 'var(--ink-soft)', fontSize: 14, margin: 0, lineHeight: 1.5 }}>{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function FeaturedLocations() {
  const [bars, setBars] = useState<Card[]>([]);

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from('ga_featured_locations')
        .select('*')
        .eq('is_active', true)
        .order('position')
        .limit(3);
      setBars((data as Card[]) ?? []);
    })();
  }, []);

  return (
    <section id="locations" className="section">
      <div className="container">
        <div style={{ marginBottom: 32 }}>
          <div className="eyebrow" style={{ marginBottom: 16 }}>Featured locations</div>
          <h2 className="display" style={{ fontSize: 'clamp(36px, 5.5vw, 76px)', margin: 0 }}>
            Find your <span className="serif-italic" style={{ color: 'var(--wine)' }}>spot.</span>
          </h2>
        </div>

        <div className="r-grid-3">
          {bars.length === 0 ? (
            <div style={{ gridColumn: '1 / -1', padding: 40, textAlign: 'center', color: 'var(--ink-soft)' }}>
              <p style={{ marginTop: 0 }}>We are onboarding locations across Manhattan.</p>
              <Link to="/request-location" className="btn btn-ghost" style={{ marginTop: 12 }}>Browse our short list →</Link>
            </div>
          ) : (
            bars.map((b) => <BarCard key={b.id} bar={b} />)
          )}
        </div>

        {bars.length > 0 && (
          <div style={{ marginTop: 32, display: 'flex', justifyContent: 'center' }}>
            <Link to="/signup" className="btn btn-primary">See all locations →</Link>
          </div>
        )}
      </div>
    </section>
  );
}

function RequestLocationBanner() {
  return (
    <section style={{ padding: 'clamp(48px, 8vw, 80px) 0' }}>
      <div className="container">
        <div className="request-banner">
          <div className="request-banner-text">
            <div className="eyebrow" style={{ marginBottom: 14, color: 'var(--primary)' }}>Got a favorite?</div>
            <h2 className="display" style={{ fontSize: 'clamp(32px, 5vw, 56px)', margin: 0, color: 'var(--bg)' }}>
              Pick our <span className="serif-italic" style={{ color: 'var(--pop)' }}>next location.</span>
            </h2>
            <p style={{ color: 'color-mix(in oklch, var(--bg) 70%, transparent)', marginTop: 16, fontSize: 'clamp(15px, 1.6vw, 17px)', maxWidth: 520 }}>
              We have 100+ Manhattan spots on the radar. Tap the one you want and we will reach out to the owner about joining Goaround.
            </p>
          </div>
          <Link to="/request-location" className="btn btn-pop request-banner-cta">Request a location →</Link>
        </div>
      </div>
      <style>{`
        .request-banner {
          background: var(--ink);
          color: var(--bg);
          border-radius: 24px;
          padding: 48px;
          display: grid;
          grid-template-columns: 1.6fr auto;
          gap: 32px;
          align-items: center;
        }
        @media (max-width: 720px) {
          .request-banner { grid-template-columns: 1fr; padding: 28px; gap: 24px; }
          .request-banner-cta { width: 100%; justify-content: center; }
        }
      `}</style>
    </section>
  );
}

function BarCard({ bar }: { bar: Card }) {
  return (
    <Link to={`/featured-locations/${bar.slug}`} className="bar-card" style={{ display: 'block' }}>
      <div
        style={{
          position: 'relative',
          width: '100%',
          aspectRatio: '4 / 3',
          borderRadius: 14,
          overflow: 'hidden',
          // Image layered over a gradient: if the image 404s the gradient still shows.
          background: bar.hero_image_url
            ? `url(${bar.hero_image_url}) center/cover no-repeat, linear-gradient(135deg, oklch(0.72 0.12 ${(bar.name.charCodeAt(0) * 7) % 360}), oklch(0.38 0.13 ${((bar.name.charCodeAt(0) * 7) + 30) % 360}))`
            : `linear-gradient(135deg, oklch(0.72 0.12 ${(bar.name.charCodeAt(0) * 7) % 360}), oklch(0.38 0.13 ${((bar.name.charCodeAt(0) * 7) + 30) % 360}))`,
        }}
      >
        <span className="mono" style={{ position: 'absolute', top: 10, left: 10, background: 'var(--bg)', color: 'var(--ink)', fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', padding: '4px 9px', borderRadius: 999 }}>
          Featured
        </span>
      </div>
      <div style={{ paddingTop: 14 }}>
        <div className="row-between">
          <h3 className="display" style={{ fontSize: 22, margin: 0 }}>{bar.name}</h3>
          {bar.neighborhood && (
            <span className="mono" style={{ fontSize: 11, letterSpacing: '0.12em', color: 'var(--ink-soft)', textTransform: 'uppercase' }}>{bar.neighborhood}</span>
          )}
        </div>
        {bar.address && (
          <div style={{ fontSize: 13, color: 'var(--ink-soft)', marginTop: 4, marginBottom: 10 }}>
            {bar.address}
          </div>
        )}
        {bar.address && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', paddingTop: 10, borderTop: '1px solid var(--line)' }}>
            <span className="display" style={{ fontSize: 22, color: 'var(--primary)' }}>→</span>
          </div>
        )}
      </div>
    </Link>
  );
}

function SocialProof() {
  const stats: { v: React.ReactNode; l: string }[] = [
    { v: '14', l: 'socials booked this month' },
    { v: '2,500', l: 'minutes of team time' },
    { v: <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 8 }}>5<StarIcon /></span>, l: 'reviews from teams' },
    { v: '107', l: 'locations on the radar' },
  ];
  return (
    <section className="section">
      <div className="container">
        <div className="eyebrow" style={{ marginBottom: 20 }}>How it's going</div>
        <div className="r-grid-4" style={{ borderTop: '1.5px solid var(--ink)', paddingTop: 28, gap: 24 }}>
          {stats.map((s) => (
            <div key={s.l}>
              <div className="display" style={{ fontSize: 'clamp(40px, 6vw, 88px)', letterSpacing: '-0.04em' }}>{s.v}</div>
              <div className="mono" style={{ fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ink-soft)', marginTop: 6 }}>{s.l}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function StarIcon() {
  return (
    <svg
      width="0.7em"
      height="0.7em"
      viewBox="0 0 24 24"
      fill="var(--primary)"
      aria-hidden
      style={{ flexShrink: 0 }}
    >
      <path d="M12 2.5l2.95 6.6 7.05.62-5.33 4.73 1.59 6.95L12 17.77l-6.26 3.63 1.59-6.95L2 9.72l7.05-.62L12 2.5z" />
    </svg>
  );
}

function FAQ() {
  const qs = [
    { q: 'Who is this for?', a: 'Team leads at NYC companies. Managers, founders, EAs, recruiters. Anyone whose job is to make the team feel valued.' },
    { q: 'Why work email only?', a: "Filters out misuse and rolls up bookings by company. One field." },
    { q: 'How does the pre-paid tab work?', a: 'We charge your card up front and settle with the bar after. Your team never sees a check.' },
    { q: 'Cancellation policy?', a: 'Full refund or free transfer up to 3pm the day of the reservation.' },
    { q: 'Where are you available?', a: 'Manhattan only, for now.' },
  ];
  return (
    <section id="faq" className="section">
      <div className="container">
        <div className="r-split-narrow">
          <div className="faq-heading">
            <div className="eyebrow" style={{ marginBottom: 16 }}>FAQ</div>
            <h2 className="display" style={{ fontSize: 'clamp(32px, 4.5vw, 60px)', margin: 0 }}>
              The <span className="serif-italic" style={{ color: 'var(--wine)' }}>real</span> questions.
            </h2>
            <p style={{ color: 'var(--ink-soft)', marginTop: 14, fontSize: 15 }}>
              Email <a href="mailto:hi@goaround.io">hi@goaround.io</a>. A human writes back same day.
            </p>
          </div>
          <div>
            {qs.map((item, i) => (
              <details key={i} style={{ borderTop: i === 0 ? '1.5px solid var(--ink)' : '1px solid var(--line)', padding: '20px 0' }}>
                <summary style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
                  <span className="display" style={{ fontSize: 20 }}>{item.q}</span>
                  <span className="faq-icon display" style={{ fontSize: 26, color: 'var(--primary)' }}>+</span>
                </summary>
                <p style={{ color: 'var(--ink-soft)', fontSize: 15, lineHeight: 1.55, marginTop: 12, marginBottom: 0, maxWidth: 640 }}>{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </div>
      <style>{`
        .faq-heading { position: sticky; top: 100px; }
        @media (max-width: 720px) {
          .faq-heading { position: static; top: auto; }
        }
      `}</style>
    </section>
  );
}

function CTAStrip() {
  return (
    <section id="book" style={{ padding: 'clamp(72px, 12vw, 120px) 0', background: 'var(--ink)', color: 'var(--bg)', position: 'relative', overflow: 'hidden' }}>
      <div aria-hidden className="display" style={{ position: 'absolute', right: -60, top: -40, fontSize: 'clamp(280px, 50vw, 540px)', color: 'color-mix(in oklch, var(--primary) 28%, transparent)', lineHeight: 0.8, pointerEvents: 'none' }}>G</div>
      <div className="container" style={{ position: 'relative' }}>
        <div style={{ maxWidth: 900 }}>
          <div className="eyebrow" style={{ marginBottom: 18, color: 'var(--primary)' }}>Plan your social</div>
          <h2 className="display" style={{ fontSize: 'clamp(40px, 7vw, 96px)', margin: 0 }}>
            Your new social booking platform is <span className="serif-italic" style={{ color: 'var(--pop)' }}>two clicks</span> away.
          </h2>
        </div>
        <div className="cta-buttons">
          <Link to="/signup" className="btn btn-pop">Sign up →</Link>
          <Link to="/login" className="btn btn-ghost" style={{ borderColor: 'var(--bg)', color: 'var(--bg)' }}>Already have an account?</Link>
        </div>

        <div className="cta-owner-row">
          <div style={{ color: 'color-mix(in oklch, var(--bg) 70%, transparent)', maxWidth: 540 }}>
            <div className="display" style={{ fontSize: 22, marginBottom: 6 }}>Own a spot?</div>
            <div style={{ fontSize: 15 }}>Get on the platform in two screens.</div>
          </div>
          <Link to="/onboarding" className="btn btn-pop">List your spot →</Link>
        </div>
      </div>
      <style>{`
        .cta-buttons { margin-top: 32px; display: flex; gap: 12px; flex-wrap: wrap; }
        .cta-owner-row {
          margin-top: 64px;
          border-top: 1px solid color-mix(in oklch, var(--bg) 20%, transparent);
          padding-top: 28px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 16px;
        }
        @media (max-width: 720px) {
          .cta-buttons { flex-direction: column; align-items: stretch; }
          .cta-buttons .btn { width: 100%; justify-content: center; }
          .cta-owner-row { align-items: flex-start; }
          .cta-owner-row .btn { width: 100%; justify-content: center; }
        }
      `}</style>
    </section>
  );
}
